import { getResponseData, type ApiClient } from "@/modules/api/apiClient.ts";
import * as billingAccountQuery from "@/modules/billing/billingAccountQuery.ts";
import * as billingHistoryQuery from "@/modules/billing/billingHistoryQuery.ts";
import * as billingInvoicesQuery from "@/modules/billing/billingInvoicesQuery.ts";
import type { EBillingAccount } from "@/modules/billing/billingLogic.tsx";
import {
    getPaymentTarget,
    type PayableInvoiceId,
    type PaymentParams,
    type PaymentResult,
} from "@/modules/billing/payment/PaymentParams.ts";
import type { PaymentPopup } from "@/modules/billing/popup/popup.ts";
import {
    t_CompletePaymentInPopupWindow,
    t_RedirectingToPaymentGateway,
    t_WaitingForPaymentConfirmation,
} from "@/modules/billing/progressLabels.tsx";
import { SimpleLocalizedError, type Localizable } from "@/modules/l10n/getTransText.ts";
import { isTestEnvironment } from "@/utils/environment.ts";
import { jsonEncodedBody } from "@/utils/fetchClient.ts";
import { invalidateQueries } from "@/utils/query/invalidateQueries.ts";
import { UnmountedError } from "@/utils/react/useUnmountSignal.tsx";
import { msg } from "@lingui/core/macro";
import type { QueryClient } from "@tanstack/query-core";
import type {
    CreditBuyBody,
    LinkParams,
    LinkParamsGeneric,
    LinkProcessorId,
    LinkSendDataBody,
    LinkSendDataResponse,
} from "@warrenio/api-spec/spec.oats.gen";
import { InvoiceStatusEnum } from "@warrenio/api-spec/spec/billingEnums";
import { notNull } from "@warrenio/utils/notNull";
import sleep from "@warrenio/utils/promise/sleep";
import invariant from "tiny-invariant";

interface RedirectAndWaitArgs {
    progress: PaymentParams["progress"];

    popup: PollOptions["popup"];
    signal: PollOptions["signal"];
    check: PollOptions["check"];

    redirectUrl: string;
}

export async function redirectToGatewayAndWaitForResponse({
    popup,
    progress,
    signal,
    redirectUrl,
    check,
}: RedirectAndWaitArgs) {
    progress(t_RedirectingToPaymentGateway);
    invariant(popup, "Popup must be provided for redirect");
    popup.handleRedirect(redirectUrl);

    progress(t_CompletePaymentInPopupWindow);
    const success = await popup.waitReturnFromRedirect(signal);

    progress(t_WaitingForPaymentConfirmation);
    // shorten default polling options
    // still do one poll when we could not determine redirect success (in case it somehow went through)
    return await pollForSuccess({
        signal,
        popup,
        pollInterval: 4000,
        maxPollCount: success ? 7 : 1,
        check,
    });
}

/** Initiate the payment process for a link-type processor */
export async function linkSendData<
    TData extends LinkSendDataResponse["data"],
    TParams extends LinkParams = LinkParamsGeneric,
>(
    apiClient: ApiClient,
    paymentParams: PaymentParams,
    processor: LinkProcessorId,
    linkParams: Omit<TParams, "referrer_url">,
): Promise<LinkSendDataResponse & { data: TData }> {
    const { account, returnUrl } = paymentParams;
    const billing_account_id = account.id;

    const extendedParams = {
        referrer_url: returnUrl,
        ...linkParams,
    } satisfies LinkParams;

    const body = {
        billing_account_id,
        ...getPaymentTarget(paymentParams),

        params: JSON.stringify(extendedParams),
        processor,
    } satisfies LinkSendDataBody;

    // TODO: Check `success` and `error_msg` fields?
    return getResponseData(
        await apiClient.POST("/payment/link/send_data", {
            ...jsonEncodedBody,
            body,
        }),
        // Cas
    ) as LinkSendDataResponse & { data: TData };
}

export async function creditBuyRequest<T extends CreditBuyBody>(apiClient: ApiClient, body: T) {
    return getResponseData(
        await apiClient.POST("/payment/credit/buy", {
            ...jsonEncodedBody,
            body,
        }),
    );
}

export async function persistentDataRequest(
    apiClient: ApiClient,
    billing_account_id: number,
    identifier: string,
    signal: AbortSignal,
) {
    return getResponseData(
        await apiClient.GET("/payment/persistent_data", {
            params: {
                query: { billing_account_id, identifier },
            },
            signal,
        }),
    );
}

export async function paymentResultRequest(
    apiClient: ApiClient,
    billing_account_id: number,
    identifier: string,
    signal: AbortSignal,
) {
    return getResponseData(
        await apiClient.GET("/payment/link/process_result", {
            params: {
                query: { billing_account_id, identifier },
            },
            signal,
        }),
    );
}

async function creditListRequest(apiClient: ApiClient, billing_account_id: number, signal: AbortSignal) {
    return getResponseData(
        await apiClient.GET("/payment/credit/list", {
            signal,
            params: { query: { billing_account_id } },
        }),
    );
}

async function invoiceRequest(apiClient: ApiClient, invoice_id: PayableInvoiceId, signal: AbortSignal) {
    return getResponseData(
        await apiClient.GET("/payment/invoice", {
            signal,
            params: { query: { invoice_id } },
        }),
    );
}

export async function finalTopupAmountRequest(
    apiClient: ApiClient,
    params: {
        billing_account_id: number;
        processor: string;
        amount: number;
    },
) {
    const { billing_account_id, processor, amount } = params;
    return getResponseData(
        await apiClient.GET("/payment/final_topup_amount", {
            params: { query: { billing_account_id, processor, amount } },
        }),
    );
}

export function getRedirectUrlFromData(data: { redirect_url: string | string[] }) {
    const { redirect_url } = data;
    const url = Array.isArray(redirect_url) ? notNull(redirect_url[0], "redirect_url") : redirect_url;
    invariant(url, "Expected redirect_url in response");
    return url;
}

export async function invalidatePaymentQueries(queryClient: QueryClient, billing_account_id: number) {
    await invalidateQueries({
        queryClient,
        immediate: [billingAccountQuery.queryKey, billingHistoryQuery.getQueryKey({ billing_account_id })],
        background: [billingInvoicesQuery.getQueryKey({ billing_account_id })],
    });
}

export interface PollOptions {
    signal: AbortSignal;
    check: () => Promise<boolean>;
    popup: PaymentPopup | null;
    pollInterval?: number;
    maxPollCount?: number;
    timeoutError?: Error;
}

export type PollForSuccessResult = Exclude<PaymentResult, typeof PaymentResult.VALIDATION_FAILED>;

export async function pollForSuccess({
    signal,
    check,
    pollInterval = 5000,
    maxPollCount = 15,
    popup,
    timeoutError = new PaymentPollTimeoutError(),
}: PollOptions): Promise<PollForSuccessResult> {
    if (isTestEnvironment) {
        // Speed up tests
        pollInterval = Math.min(pollInterval, 500);
    }

    try {
        // NB: This loop is exited with an abort error whenever the signal is set (eg. when the component is unmounted)
        for (let pollCount = 0; ; pollCount++) {
            if (maxPollCount != null && pollCount > maxPollCount) {
                throw timeoutError;
            }
            await sleep(pollInterval, signal);

            if (popup) {
                console.debug("%s: Checking popup state: %o", pollCount, popup.toString());
            }

            if (await check()) {
                return "SUCCESS";
            }
        }
    } catch (e) {
        if (e instanceof UnmountedError) {
            console.debug("Unmounted, stopping polling");
            return "CANCELLED";
        } else if (e instanceof PaymentPollTimeoutError) {
            // response is asynchronous, takes long time and it's not useful for a user to wait it out
            console.debug("Timeout, stopping polling");
            return "WAIT_RESPONSE";
        } else {
            throw e;
        }
    }
}

/** @public */
export class PaymentPollTimeoutError extends SimpleLocalizedError {
    name = "PaymentPollTimeoutError";

    constructor() {
        super(
            msg`Response seems to be taking more time than expected. Wait a bit and refresh the page to see payment status changes reflected. If it doesn't happen in a reasonable time, please contact support.`,
        );
    }
}

export function failureToMessage({
    failure_code,
    failure_message,
}: {
    failure_code?: string;
    failure_message?: string;
}): Localizable {
    const codeSuffix = failure_code ? ` [${failure_code}]` : "";
    if (failure_message) {
        return msg`Payment error: ${failure_message}${codeSuffix}`;
    }
    return msg`Unknown payment error${codeSuffix}`;
}

async function checkForCreditReference({
    account,
    apiClient,
    signal,
    reference,
}: {
    account: EBillingAccount;
    apiClient: ApiClient;
    signal: AbortSignal;
    reference: string;
}): Promise<boolean> {
    // XXX: This loads all credits, which is inefficient
    const creditList = await creditListRequest(apiClient, account.id, signal);
    return creditList.some((c) => c.reference === reference);
}

export async function checkForPaymentTargetSuccess({
    apiClient,
    reference,
    params,
    signal,
}: {
    apiClient: ApiClient;
    reference: string;
    params: PaymentParams;
    signal: AbortSignal;
}): Promise<boolean> {
    if (params.target.type === "invoice") {
        const invoice = await invoiceRequest(apiClient, params.target.invoice_id, signal);
        return invoice.status === InvoiceStatusEnum.PAID;
    }

    return await checkForCreditReference({
        account: params.account,
        apiClient,
        signal,
        reference,
    });
}
